import { createSlice, type PayloadAction } from '@ufc/core'

import type { DeliveryRouteDto, PlanRequest, PlanResponse } from '../domain/types/api.types'
import type { PlanningSettings, ReadinessResponse } from '../infrastructure/api/trip-planning-api'

export interface TripPlannerState {
  routes: DeliveryRouteDto[]
  readiness: ReadinessResponse | null
  settings: PlanningSettings | null
  plan: PlanResponse | null
  routesLoading: boolean
  readinessLoading: boolean
  planning: boolean
  committing: boolean
  savingSettings: boolean
  committed: number | null
  error: string | null
}

const initialState: TripPlannerState = {
  routes: [], readiness: null, settings: null, plan: null,
  routesLoading: false, readinessLoading: false,
  planning: false, committing: false, savingSettings: false, committed: null, error: null,
}

const slice = createSlice({
  name: 'tripPlanner',
  initialState,
  reducers: {
    loadWorkspace: (state) => {
      state.routesLoading = true; state.readinessLoading = true; state.error = null
    },
    loadWorkspaceSuccess: (state, action: PayloadAction<{ routes: DeliveryRouteDto[]; readiness: ReadinessResponse; settings: PlanningSettings }>) => {
      state.routes = action.payload.routes; state.readiness = action.payload.readiness; state.settings = action.payload.settings
      state.routesLoading = false; state.readinessLoading = false
    },
    loadWorkspaceFailure: (state, action: PayloadAction<string>) => {
      state.routesLoading = false; state.readinessLoading = false; state.error = action.payload
    },
    refreshReadiness: (state) => { state.readinessLoading = true; state.error = null },
    refreshReadinessSuccess: (state, action: PayloadAction<ReadinessResponse>) => {
      state.readiness = action.payload; state.readinessLoading = false
    },
    refreshReadinessFailure: (state, action: PayloadAction<string>) => {
      state.readinessLoading = false; state.error = action.payload
    },
    runPlan: (state, _action: PayloadAction<PlanRequest>) => {
      state.planning = true; state.committed = null; state.error = null
    },
    runPlanSuccess: (state, action: PayloadAction<PlanResponse>) => {
      state.plan = action.payload; state.planning = false
    },
    runPlanFailure: (state, action: PayloadAction<string>) => {
      state.planning = false; state.error = action.payload
    },
    commit: (state, _action: PayloadAction<PlanRequest>) => {
      state.committing = true; state.error = null
    },
    commitSuccess: (state, action: PayloadAction<{ written: number; readiness: ReadinessResponse }>) => {
      state.committing = false; state.committed = action.payload.written; state.readiness = action.payload.readiness
    },
    commitFailure: (state, action: PayloadAction<string>) => {
      state.committing = false; state.error = action.payload
    },
    saveSettings: (state, _action: PayloadAction<PlanningSettings>) => {
      state.savingSettings = true; state.error = null
    },
    saveSettingsSuccess: (state, action: PayloadAction<PlanningSettings>) => {
      state.settings = action.payload; state.savingSettings = false; state.plan = null; state.committed = null
    },
    saveSettingsFailure: (state, action: PayloadAction<string>) => {
      state.savingSettings = false; state.error = action.payload
    },
    clearPlan: (state) => { state.plan = null; state.committed = null },
  },
})

export const tripPlannerActions = slice.actions
export const tripPlannerReducer = slice.reducer
